"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  mobile = false,
  onLinkClick
}: {
  mobile?: boolean;
  onLinkClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [activePath, setActivePath] = useState<string>("/");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setActivePath(window.location.pathname);
    }
  }, []);
  if (loading) {
    return <div className={cn("flex gap-4", mobile ? "flex-col" : "flex-row")}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  if (mobile) {
    return <nav className="flex flex-col space-y-6">
        {menuItems.map((item, i) => {
        const isActive = item.href === activePath || item.href === "/" && activePath === "/";
        return <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className={cn("block text-lg font-medium transition-colors", isActive ? "text-primary" : "text-foreground hover:text-primary")} onClick={onLinkClick}>
                {item.label}
              </Link>
            </div>;
      })}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        const isActive = item.href === activePath || item.href === "/" && activePath === "/";
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn("text-sm font-medium transition-all duration-300", isActive ? "bg-primary text-primary-foreground rounded-full px-4 py-1.5 shadow-sm" : "text-foreground hover:text-primary relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full")}>
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function PrimaryNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="primary-navigation" className="dark relative z-50">
      {}
      <header className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-border/10 py-3 shadow-sm" : "bg-background py-5")}>
        <div className="container mx-auto px-6 md:px-8 flex items-center justify-between">
          {}
          <Link to="/" className="flex-shrink-0 z-50">
            <img src="https://joewestinspections.com/wp-content/uploads/2026/03/Screenshot-2026-03-06-at-10.21.27-AM.png" alt="JW Construction Logo" className="!h-[40px]" />
          </Link>

          {}
          <div className="hidden md:block">
            <MenuProvider menu_id="46">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:hidden z-50">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent/10" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {}
      <div className={cn("fixed inset-0 z-40 bg-background/95 backdrop-blur-xl transition-all duration-300 md:hidden flex flex-col justify-center px-8", isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-4")}>
        <div className="flex flex-col space-y-8">
          <MenuProvider menu_id="46">
            <HeaderMenuConsumer mobile onLinkClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          
          <div className="pt-8 border-t border-border/20">
             <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-base py-6">
                Get Started
             </Button>
          </div>
        </div>
      </div>
      
      {}
      <div className="h-20" />
    </section>;
}